<template>
    <vx-expandable-card id="groups">
        <template v-slot:header> Groups </template>

        <template v-slot:content>
            <div class="flex gap-2 mb-3 lg:mb-6">
                <div class="grow" data-test="group-id-wrapper">
                    <vx-selectable
                        size="lg"
                        placeholder=""
                        :options="options"
                        :loading="searching"
                        :reduce="(option) => option.value"
                        :createOption="
                            (option) => {
                                return {
                                    label: option,
                                    value: option,
                                };
                            }
                        "
                        :getOptionKey="(option) => option.value"
                        :taggable="true"
                        @search="search"
                        v-model="groupId"
                        @option:selected="add()"
                        :error="displayFirstError(validate.groupId)"
                    >
                        <template v-slot:no-options> Search groups. </template>
                    </vx-selectable>
                </div>
                <vx-button @click="add()" data-test="group-add" size="lg" type="button">
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
            <vx-icon-loading v-if="loadingGroups" spin></vx-icon-loading>
            <vx-alert :closeable="false" v-else-if="!groups.length">
                No groups were specified for this contact.
            </vx-alert>
            <div v-else>
                <vx-list-animated v-model="groups" label="group_id" value="group_id">
                    <template v-slot:item="{ item }">
                        <div
                            class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 md:grid-cols-5 md:gap-2"
                        >
                            <div
                                :class="{
                                    'md:col-span-5': !item.is_preferred,
                                    'md:col-span-4': item.is_preferred,
                                }"
                            >
                                <vx-popover
                                    hover
                                    arrow
                                    placement="top"
                                    class="[&>div:first-child]:line-clamp-3 break-all text-xs"
                                >
                                    {{ item.group_name || item.group_id }}
                                    <template v-slot:content>
                                        {{ item.group_id }}
                                    </template>
                                </vx-popover>
                            </div>
                            <div
                                v-if="item.is_preferred"
                                class="flex justify-start w-full text-xs mt-1.5 md:mt-0 md:justify-end"
                            >
                                <vx-badge size="xs" color="violet">
                                    Preferred <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                                </vx-badge>
                            </div>
                        </div>
                    </template>
                </vx-list-animated>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { faCirclePlus, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxIconLoading,
    VxListAnimated,
    VxSelectable,
    VxPopover,
} from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { nextTick, ref } from 'vue';
import { alphanumericDashUnderscore, displayFirstError } from '~/utils/validation';
import { useContactDetails } from '../useContactDetails';
import debounce from '~/utils/debounce';
import { groupsClient } from '~/services';

const teamId = Spark.state.currentTeam.id;

const { contact, groups, loadingGroups } = useContactDetails();

const groupId = ref('');
const adding = ref(false);

const validate = useVuelidate(
    {
        groupId: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(39),
            format: alphanumericDashUnderscore,
        },
    },
    {
        groupId: groupId,
    }
);

const searching = ref(false);
const options = ref([]);
const search = debounce((search) => {
    searching.value = true;

    groupsClient
        .getGroups(
            { teamId },
            {
                query: search,
                per_page: 10,
                setup_required: 0,
            }
        )
        .then((response) => {
            options.value = response.data.data.map(({ group_id, name }) => ({
                value: group_id,
                label: `${name} - ${group_id}`.trim(),
            }));
            searching.value = false;
        });
}, 200);

const add = async () => {
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();
    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    if (!groups.value.find((group) => group.group_id === groupId.value)) {
        const existingGroupOption = options.value.find((option) => option.value === groupId.value);

        groups.value.unshift({
            team_id: teamId,
            contact_id: contact.value.id,
            group_id: existingGroupOption?.value || groupId.value,
            group_name: existingGroupOption?.label || '',
        });
    }

    groupId.value = '';
    adding.value = false;
};
</script>
